import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Box,
  Typography,
  Autocomplete,
  InputAdornment,
  Alert
} from '@mui/material';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import { ConcepteIcon } from '../common/IconMap';
import { capitalize } from '../../functions/formatString';

/**
 * Diàleg per crear o editar una Regla Avançada d'Assignació (regles-assignacio).
 */
const ReglaAssignacioDialog = ({
  open,
  onClose,
  onSave,
  initialRule = null,
  existingKeys = [],
  conceptesList = []
}) => {
  const isEditing = Boolean(initialRule);

  const [nom, setNom] = useState('');
  const [moviment, setMoviment] = useState('');
  const [importVal, setImportVal] = useState('');
  const [dayMode, setDayMode] = useState('dia'); // 'dia' | 'ultim-dia'
  const [dia, setDia] = useState('');
  const [toleranciaDies, setToleranciaDies] = useState(0);
  const [concepte, setConcepte] = useState('');
  const [descripcio, setDescripcio] = useState('');
  const [enabled, setEnabled] = useState(true);

  // Errors de validació
  const [errors, setErrors] = useState({});

  // Helper per generar slugs en kebab-case
  const toKey = (text) => {
    return (text || '')
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  useEffect(() => {
    if (open) {
      if (initialRule) {
        setNom(initialRule.nom || '');
        setMoviment(initialRule.moviment || '');
        const amount = initialRule.import !== undefined ? initialRule.import : (initialRule.importe !== undefined ? initialRule.importe : '');
        setImportVal(amount !== '' ? String(amount) : '');
        
        if (initialRule['ultim-dia'] === true) {
          setDayMode('ultim-dia');
          setDia('');
        } else {
          setDayMode('dia');
          setDia(initialRule.dia !== null && initialRule.dia !== undefined ? String(initialRule.dia) : '1');
        }

        const tol = initialRule['tolerancia-dies'] !== undefined
          ? initialRule['tolerancia-dies']
          : (initialRule.toleranciaDies !== undefined ? initialRule.toleranciaDies : 0);
        setToleranciaDies(tol);
        setConcepte(capitalize(initialRule.concepte || ''));
        setDescripcio(initialRule.descripcio || '');
        setEnabled(initialRule.enabled ?? true);
      } else {
        // Estat inicial per a nova regla
        setNom('');
        setMoviment('');
        setImportVal('');
        setDayMode('dia');
        setDia('1');
        setToleranciaDies(0);
        setConcepte('');
        setDescripcio('');
        setEnabled(true);
      }
      setErrors({});
    }
  }, [open, initialRule]);

  // Gestió de l'identificador mentre s'escriu (permetent guió al final)
  const handleNomChange = (e) => {
    const raw = e.target.value || '';
    const sanitized = raw
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/-{2,}/g, '-');

    setNom(sanitized);
    if (errors.nom) {
      setErrors((prev) => ({ ...prev, nom: null }));
    }
  };

  const handleNomBlur = () => {
    setNom((prev) => prev.replace(/^-+|-+$/g, ''));
  };

  // Si l'usuari no ha escrit manualment el nom en mode creació, en suggerim un a partir del concepte
  const handleConcepteChange = (newConcepte) => {
    const formatted = newConcepte ? capitalize(newConcepte) : '';
    setConcepte(formatted);
    if (!isEditing && (!nom || nom === toKey(concepte))) {
      setNom(toKey(formatted));
    }
  };

  const validate = () => {
    const newErrors = {};

    const cleanNom = nom.replace(/^-+|-+$/g, '').trim();
    if (!cleanNom) {
      newErrors.nom = "L'identificador de la regla és obligatori.";
    } else if (!isEditing && existingKeys.includes(cleanNom)) {
      newErrors.nom = 'Ja existeix una regla amb aquest identificador.';
    }

    if (!moviment.trim()) {
      newErrors.moviment = 'El text del moviment és obligatori.';
    }

    if (importVal === '' || isNaN(Number(importVal))) {
      newErrors.importVal = "L'import ha de ser un valor numèric vàlid (ex: -60 o 1000).";
    }

    if (dayMode === 'dia') {
      const diaNum = parseInt(dia, 10);
      if (isNaN(diaNum) || diaNum < 1 || diaNum > 31) {
        newErrors.dia = 'El dia del mes ha d’estar comprès entre 1 i 31.';
      }
    }

    const tolNum = parseInt(toleranciaDies, 10);
    if (isNaN(tolNum) || tolNum < 0) {
      newErrors.toleranciaDies = 'La tolerància ha de ser un número positiu o 0.';
    }

    if (!concepte.trim()) {
      newErrors.concepte = 'El concepte de destí és obligatori.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;

    const cleanNom = nom.replace(/^-+|-+$/g, '').trim();
    const parsedImport = parseFloat(Number(importVal).toFixed(2));
    const isUltimDia = dayMode === 'ultim-dia';
    const parsedDia = isUltimDia ? null : parseInt(dia, 10);
    const parsedTol = parseInt(toleranciaDies, 10) || 0;

    const ruleData = {
      nom: cleanNom,
      moviment: moviment.trim(),
      import: parsedImport,
      dia: parsedDia,
      'ultim-dia': isUltimDia,
      'tolerancia-dies': parsedTol,
      concepte: concepte.trim().toUpperCase(),
      descripcio: descripcio.trim() ? descripcio.trim() : null,
      enabled: Boolean(enabled)
    };

    onSave(ruleData, initialRule?.nom);
  };

  const conceptOptions = (conceptesList || []).map((c) => (typeof c === 'string' ? c : c.nom)).filter(Boolean);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <EventRepeatIcon color="primary" />
        <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
          {isEditing ? 'Editar Regla Avançada' : 'Nova Regla Avançada'}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Identificador únic */}
          <TextField
            label="Identificador de la regla (nom)"
            value={nom}
            onChange={handleNomChange}
            onBlur={handleNomBlur}
            placeholder="ex: pis-manresa, inversions-sp500"
            helperText={errors.nom || 'Clau única en format slug (kebab-case) per identificar la regla.'}
            error={Boolean(errors.nom)}
            disabled={isEditing}
            fullWidth
            size="small"
            required
          />

          {/* Text del moviment */}
          <TextField
            label="Text del moviment bancari"
            value={moviment}
            onChange={(e) => setMoviment(e.target.value)}
            placeholder="ex: PAGAMENT TRASPASSOS, PAGAMENT TRANSFER."
            helperText={errors.moviment || 'Text literal o patró contingut en la descripció del banc.'}
            error={Boolean(errors.moviment)}
            fullWidth
            size="small"
            required
          />

          {/* Import */}
          <TextField
            label="Import (€)"
            type="number"
            value={importVal}
            onChange={(e) => setImportVal(e.target.value)}
            placeholder="ex: -60.00 o 1200.00"
            helperText={errors.importVal || 'Usa valors negatius per a càrrecs/despeses i positius per a ingressos.'}
            error={Boolean(errors.importVal)}
            fullWidth
            size="small"
            required
            InputProps={{
              endAdornment: <InputAdornment position="end">€</InputAdornment>
            }}
          />

          {/* Planificació temporal (dia / últim dia) */}
          <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 0.5 }}>
                Planificació d'Execució en el Mes
              </FormLabel>
              <RadioGroup
                row
                value={dayMode}
                onChange={(e) => setDayMode(e.target.value)}
              >
                <FormControlLabel
                  value="dia"
                  control={<Radio size="small" />}
                  label="Dia fix del mes"
                />
                <FormControlLabel
                  value="ultim-dia"
                  control={<Radio size="small" />}
                  label="Últim dia del mes"
                />
              </RadioGroup>
            </FormControl>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1 }}>
              {dayMode === 'dia' && (
                <TextField
                  label="Dia del mes (1 - 31)"
                  type="number"
                  value={dia}
                  onChange={(e) => setDia(e.target.value)}
                  inputProps={{ min: 1, max: 31, step: 1 }}
                  helperText={errors.dia || 'Dia concret de cobrament/pagament.'}
                  error={Boolean(errors.dia)}
                  fullWidth
                  size="small"
                  required
                />
              )}
              <TextField
                label="Tolerància de dies (marge)"
                type="number"
                value={toleranciaDies}
                onChange={(e) => setToleranciaDies(e.target.value)}
                inputProps={{ min: 0, max: 15, step: 1 }}
                helperText={errors.toleranciaDies || 'Marge de dies si cau en festiu o cap de setmana (0 = dia exacte).'}
                error={Boolean(errors.toleranciaDies)}
                fullWidth
                size="small"
              />
            </Box>
          </Box>

          {/* Concepte Destí */}
          <Autocomplete
            freeSolo
            options={conceptOptions}
            value={concepte}
            getOptionLabel={(option) => capitalize(option || '')}
            onChange={(_, val) => handleConcepteChange(val)}
            onInputChange={(_, val, reason) => {
              if (reason === 'input') {
                handleConcepteChange(val);
              }
            }}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ConcepteIcon concept={option} sx={{ fontSize: 20 }} />
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Concepte de Destí"
                placeholder="ex: Pis Manresa, Inversions, Aliens..."
                helperText={errors.concepte || 'Categoria que s’assignarà automàticament al moviment.'}
                error={Boolean(errors.concepte)}
                fullWidth
                size="small"
                required
                InputProps={{
                  ...params.InputProps,
                  startAdornment: concepte ? (
                    <InputAdornment position="start">
                      <ConcepteIcon concept={concepte} onlyIcon sx={{ fontSize: 20 }} />
                    </InputAdornment>
                  ) : null
                }}
              />
            )}
          />

          {/* Descripció Opcional */}
          <TextField
            label="Descripció complementària (opcional)"
            value={descripcio}
            onChange={(e) => setDescripcio(e.target.value)}
            placeholder="ex: Joana, Marti, sp500"
            helperText="Text que s’afegirà a la descripció del moviment categoritzat."
            fullWidth
            size="small"
          />

          {/* Switch Activa */}
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                color="success"
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {enabled ? 'Regla Activada' : 'Regla Desactivada'}
              </Typography>
            }
          />

          {/* Avis informatiu sobre persistència */}
          <Alert severity="info" variant="outlined" sx={{ mt: 1, fontSize: '0.82rem' }}>
            En desar, aquesta regla s'afegirà a la configuració d'aquesta pantalla. Recorda prémer el botó <strong>"Guardar Canvis"</strong> per persistir-la a la base de dades.
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel·lar
        </Button>
        <Button variant="contained" color="primary" onClick={handleSave}>
          {isEditing ? 'Desar Canvis' : 'Crear Regla'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReglaAssignacioDialog;
